import { Card, CardHeader, CardTitle } from '@growdash/design-system/atoms';
import { Chart } from '@growdash/design-system/molecules';
import { Brand } from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { Loader2 } from 'lucide-react';

type Props = {
  firstBrandData?: Brand;
  secondBrandData?: Brand;
  isEmptyState?: boolean;
  isLoading?: boolean;
};

export default function RadarView({
  firstBrandData,
  secondBrandData,
  isEmptyState = false,
  isLoading,
}: Props) {
  const currency = useCurrency();

  const minMax = {
    avg_price: {
      min: 0,
      max: Math.max(
        firstBrandData?.avg_price || 0,
        secondBrandData?.avg_price || 0,
      ),
    },
    avg_delivery_time: {
      min: 0,
      max: Math.max(
        firstBrandData?.avg_delivery_time ?? 0,
        secondBrandData?.avg_delivery_time ?? 0,
      ),
    },
    avg_rating_score: { min: 0, max: 5 },
    total_rating_count: {
      min: 0,
      max: Math.max(
        firstBrandData?.total_rating_count ?? 0,
        secondBrandData?.total_rating_count ?? 0,
      ),
    },
    total_items: {
      min: 0,
      max: Math.max(
        firstBrandData?.total_items || 0,
        secondBrandData?.total_items || 0,
      ),
    },
  };

  const normalize = (
    value: number | null | undefined,
    min: number,
    max: number,
  ) => {
    if (value === undefined || value === null) return 0;

    // Ensure value is never negative for ratings
    const clampedValue = Math.max(0, value);

    return max === min ? 0 : ((clampedValue - min) / (max - min)) * 100;
  };

  const generateNormalizedChartData = (brand: Brand) => {
    if (!brand)
      return {
        normalizedValues: null,
        realValues: null,
      };

    const {
      avg_price = 0,
      avg_delivery_time = 0,
      avg_rating_score = 0,
      total_rating_count = 0,
      total_items = 0,
    } = brand;

    // Ensure rating score is clamped between 0 and 5
    const clampedRatingScore = Math.max(0, Math.min(5, avg_rating_score ?? 0));

    const normalizedValues = [
      normalize(
        Math.max(0, avg_price ?? 0),
        minMax.avg_price.min,
        minMax.avg_price.max,
      ),
      normalize(
        Math.max(0, avg_delivery_time ?? 0),
        minMax.avg_delivery_time.min,
        minMax.avg_delivery_time.max,
      ),
      normalize(
        clampedRatingScore,
        minMax.avg_rating_score.min,
        minMax.avg_rating_score.max,
      ),
      normalize(
        Math.max(0, total_rating_count ?? 0),
        minMax.total_rating_count.min,
        minMax.total_rating_count.max,
      ),
      normalize(
        Math.max(0, total_items ?? 0),
        minMax.total_items.min,
        minMax.total_items.max,
      ),
    ];
    const realValues = [
      Math.max(0, avg_price ?? 0),
      Math.max(0, avg_delivery_time ?? 0),
      clampedRatingScore,
      Math.max(0, total_rating_count ?? 0),
      Math.max(0, total_items ?? 0),
    ];
    return { normalizedValues, realValues };
  };

  const firstBFormatted = generateNormalizedChartData(
    firstBrandData ?? ({} as Brand),
  );
  const secondBFormatted = generateNormalizedChartData(
    secondBrandData as Brand,
  );

  // Format values based on category
  const formatValue = (value: number, category: string) => {
    if (value === undefined || value === null) return 'N/A';

    switch (category) {
      case 'Price':
        return `${value.toFixed(2)} ${currency}`;
      case 'Delivery Time':
        return `${value.toFixed(0)} min`;
      case 'Rating':
        return value.toFixed(1);
      case 'Total Ratings':
      case 'Item Count':
        return value.toLocaleString();
      default:
        return value.toString();
    }
  };

  const emptyStateSerie = Array.from({ length: 5 }, () => 0);

  const chartOptions = {
    series: [
      {
        name: firstBrandData?.brand_name ?? 'Brand A',
        data: isEmptyState ? emptyStateSerie : firstBFormatted.normalizedValues,
      },
      {
        name: secondBrandData?.brand_name ?? 'Brand B',
        data: isEmptyState
          ? emptyStateSerie
          : secondBFormatted.normalizedValues,
      },
    ],
    colors: ['#c27aff', '#ff8904'],
    chart: {
      type: 'radar',
      toolbar: {
        show: false,
      },
    },
    stroke: {
      width: 2,
    },
    fill: {
      opacity: 0.1,
    },
    markers: {
      size: isEmptyState ? 0 : 4,
    },
    xaxis: {
      categories: [
        'Price',
        'Delivery Time',
        'Rating',
        'Total Ratings',
        'Item Count',
      ],
    },
    yaxis: {
      show: false,
      marker: {
        show: !isEmptyState,
      },
    },
    tooltip: {
      enabled: true,
      x: {
        show: true,
      },
      marker: {
        show: !isEmptyState,
      },
      fixed: {
        enabled: false,
      },
      custom: function ({
        dataPointIndex,
      }: {
        series: any;
        seriesIndex: number;
        dataPointIndex: number;
        w: any;
      }) {
        const categories = [
          'Price',
          'Delivery Time',
          'Rating',
          'Total Ratings',
          'Item Count',
        ];
        if (isEmptyState) return null;
        const category = categories[dataPointIndex];
        const firstBrandVal = firstBFormatted.realValues?.[dataPointIndex] ?? 0;
        const secondBrandVal =
          secondBFormatted.realValues?.[dataPointIndex] ?? 0;

        const formattedFirstBrandVal = formatValue(firstBrandVal, category);
        const formattedSecondBrandVal = formatValue(secondBrandVal, category);

        return (
          '<div class="custom-tooltip" style="padding: 8px;">' +
          '<div style="font-weight: bold; margin-bottom: 5px">' +
          category +
          '</div>' +
          '<div style="display: flex; align-items: center; margin-bottom: 5px;">' +
          '<span style="display: inline-block; width: 10px; height: 10px; background: #c27aff; margin-right: 5px; border-radius: 50%"></span>' +
          '<span>' +
          (!isEmptyState ? firstBrandData?.brand_name : '') +
          ': ' +
          formattedFirstBrandVal +
          '</span>' +
          '</div>' +
          '<div style="display: flex; align-items: center;">' +
          '<span style="display: inline-block; width: 10px; height: 10px; background: #ff8904; margin-right: 5px; border-radius: 50%"></span>' +
          '<span>' +
          (!isEmptyState ? secondBrandData?.brand_name : '') +
          ': ' +
          formattedSecondBrandVal +
          '</span>' +
          '</div>' +
          '</div>'
        );
      },
    },
  };

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="">Analytics</CardTitle>
      </CardHeader>
      {isLoading ? (
        <div className="animate-fade-in">
          <div className="animate-pulse py-6 flex justify-center items-center h-[400px]">
            <Loader2 className="animate-spin" />
          </div>
        </div>
      ) : (
        <Chart
          className="animate-fade-in maw-w-full"
          type="radar"
          height={400}
          options={chartOptions}
          series={chartOptions['series']}
        />
      )}
    </Card>
  );
}
